import { useTranslation } from 'react-i18next';
import { Activity, AlertTriangle, BedDouble, CheckCircle2, Clock, DollarSign, Sparkles, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import type { DisruptionDetail, DisruptionProgress, ResolutionState } from '@/lib/disruptions/types';
import { formatPriceMinor, isPriced } from '../labels';
import { priceHint } from '../disruption-resolution-cockpit';

interface DisruptionPulseBridgeProps {
  detail: DisruptionDetail;
  state: ResolutionState;
  live: DisruptionProgress | undefined;
}

export function DisruptionPulseBridge({ state, live }: DisruptionPulseBridgeProps) {
  const { t } = useTranslation('tower');
  const { kpis } = state;
  const g = kpis.groups;
  const totalPax = kpis.affectedPassengers || 1;
  const assignedPax = g?.assignedPax ?? (g?.assigned ? g.assigned * 1.8 : 0);
  
  // Calculate Protection Index (% of pax accommodated or ticketed)
  const protectionPct = Math.min(100, Math.round((assignedPax / totalPax) * 100));
  
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;
  const isRunning = !!activeRun;
  const isContained = state.steps.find((s) => s.op === 'contain')?.status === 'DONE';
  const hasProblems = Object.values(state.problems.noOptionsByReason ?? {}).some((c) => c > 0) || 
                      state.problems.allocationGaps > 0 || 
                      (state.problems.declined ?? 0) > 0;

  // airline-money@v2 — the airline's PRICE; null → «No disponible», never $0.
  const priceFormatted = formatPriceMinor(kpis.totalPriceMinor, kpis.currency, t);
  const priced = isPriced(kpis.totalPriceMinor);

  return (
    <div className="flex flex-col gap-3" data-testid="disruption-pulse-bridge">
      {/* Top Banner: Status & Protection Metric Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        
        {/* Metric 1: Passenger Sanctuary / Protection Index */}
        <Card className={cn(
          "relative overflow-hidden border transition-all duration-300 shadow-sm",
          isContained 
            ? "border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20" 
            : protectionPct >= 90 
              ? "border-emerald-500/30 bg-card"
              : "border-border bg-card"
        )}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Users className="h-3.5 w-3.5 text-indigo-500" />
                <span>{t('alt.pulse.protectionIndex')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold tracking-tight text-foreground">
                  {protectionPct}%
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('alt.pulse.protectionCount', {
                    assigned: Math.round(assignedPax),
                    total: kpis.affectedPassengers,
                  })}
                </span>
              </div>
              <div className="h-1.5 w-full min-w-[120px] overflow-hidden rounded-full bg-muted">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    protectionPct >= 90 ? "bg-emerald-500" : protectionPct >= 50 ? "bg-indigo-500" : "bg-amber-500"
                  )}
                  style={{ width: `${protectionPct}%` }}
                />
              </div>
            </div>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-950/50">
              {isContained ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              ) : protectionPct >= 90 ? (
                <Sparkles className="h-6 w-6 text-emerald-500 animate-pulse" />
              ) : (
                <TrendingUp className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Metric 2: Live Operational Pulse / Velocity */}
        <Card className={cn(
          "relative overflow-hidden border transition-all duration-300 shadow-sm",
          isRunning ? "border-sky-500/40 bg-sky-50/40 dark:bg-sky-950/20" : "border-border bg-card"
        )}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Activity className={cn("h-3.5 w-3.5", isRunning ? "animate-spin text-sky-500" : "text-emerald-500")} />
                <span>{t('alt.pulse.operationalPulse')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold tracking-tight text-foreground">
                  {isRunning
                    ? t('alt.pulse.running')
                    : isContained
                      ? t('alt.pulse.contained')
                      : t('alt.pulse.ready')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('alt.pulse.flightsGroups', {
                    flights: t('count.flights', { count: kpis.caseCount }),
                    groups: g ? g.groups : '—',
                  })}
                </span>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {isRunning ? (
                  <span className="font-medium text-sky-600 dark:text-sky-400">
                    {t('alt.pulse.engineRunning')}
                  </span>
                ) : isContained ? (
                  <span className="text-emerald-600 dark:text-emerald-400">
                    {t('alt.pulse.eventClosed')}
                  </span>
                ) : (
                  <span>{t('alt.pulse.awaiting')}</span>
                )}
              </p>
            </div>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-50 dark:bg-sky-950/50">
              <Clock className="h-6 w-6 text-sky-600 dark:text-sky-400" />
            </div>
          </CardContent>
        </Card>

        {/* Metric 3: Financial Command & Hotel Spend */}
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="flex items-center justify-between p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                <span>{t('alt.pulse.budget')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  className={cn(
                    'tracking-tight',
                    priced ? 'text-2xl font-bold text-foreground' : 'text-base font-semibold text-muted-foreground',
                  )}
                >
                  {priceFormatted}
                </span>
              </div>
              <p className="truncate text-xs text-muted-foreground" title={priceHint(kpis)}>
                {!priced
                  ? priceHint(kpis)
                  : kpis.priceSource === 'actual+estimate'
                    ? t('alt.pulse.priceBreakdown', {
                        held: formatPriceMinor(kpis.heldPriceMinor ?? 0, kpis.currency, t),
                        booked: formatPriceMinor(kpis.bookedPriceMinor ?? 0, kpis.currency, t),
                      })
                    : t('alt.pulse.priceEstimate')}
              </p>
            </div>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-950/50">
              <BedDouble className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        {/* Metric 4: Anomaly & Exception Radar */}
        <Card className={cn(
          "border transition-all duration-300 shadow-sm",
          hasProblems ? "border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/20" : "border-border bg-card"
        )}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <AlertTriangle className={cn("h-3.5 w-3.5", hasProblems ? "text-amber-500" : "text-slate-400")} />
                <span>{t('alt.pulse.exceptionRadar')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={cn("text-2xl font-bold tracking-tight", hasProblems ? "text-amber-600 dark:text-amber-400" : "text-foreground")}>
                  {hasProblems ? t('alt.pulse.attentionRequired') : t('alt.pulse.noBlockers')}
                </span>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {hasProblems ? t('alt.pulse.specialCases') : t('alt.pulse.fullyAutomated')}
              </p>
            </div>
            <div className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
              hasProblems ? "bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400" : "bg-muted text-muted-foreground"
            )}>
              <AlertTriangle className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
